/**
 * Camp Name Formatter
 * Utility functions for formatting camp names with hierarchical display logic
 * Supports: Organization → Camp → Program → Session
 */

import { shouldShowSession } from './sessionExpiryHelpers';

/**
 * Format camp display name based on available hierarchy levels
 * Display rules:
 * - If only Camp: return camp.name
 * - If Camp + Program: return "camp.name - camp.program"
 * - If Camp + Session (not expired): return "camp.name - camp.session"
 * - If Camp + Program + Session (not expired): return "camp.name - camp.session" (don't show program)
 * - If Session is expired: fall back to Camp or Camp + Program (don't show expired session)
 * 
 * @param {Object} camp - Camp object from camp_audit_data or user_programs.program_data
 * @returns {string} Formatted camp display name
 */
export function formatCampDisplayName(camp) {
  if (!camp) {
    return '';
  }
  
  // Extract name (could be from camp_audit_data or user_programs.program_data)
  const campName = camp.name || camp.title || camp.program_data?.name || '';
  
  if (!campName) {
    return '';
  }
  
  // Check if session exists and is not expired
  // Check both top-level and program_data for session/program fields
  const program = camp.program || camp.program_data?.program || '';
  const session = camp.session || camp.program_data?.session || '';
  
  // For shouldShowSession, we need to pass the full camp object (it checks program_data internally)
  const showSession = shouldShowSession(camp);
  
  // If we have a valid session, show Camp - Session (don't show program)
  if (showSession && session) {
    return `${campName} - ${session}`;
  }
  
  // If we have a program, show Camp - Program
  if (program) {
    return `${campName} - ${program}`;
  }
  
  // Otherwise, just show camp name
  return campName;
}

/**
 * Check if a session is expired (re-exported from sessionExpiryHelpers for convenience)
 * @param {Object} camp - Camp object with session_expires_at field
 * @returns {boolean} True if session is expired
 */
export { isSessionExpired } from './sessionExpiryHelpers';

/**
 * Get current session year (re-exported from sessionExpiryHelpers for convenience)
 * @returns {number} Current year
 */
export { getCurrentSessionYear } from './sessionExpiryHelpers';
